import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Calendar, Trophy, DollarSign,
  Mail, Phone, User, ArrowRight, Loader2, X, Check, Star
} from 'lucide-react';

interface MembershipPageProps {
  onNavigate?: (page: string) => void;
}

interface MembershipModalData {
  title: string;
  pricing: string[];
  notice: string;
}

const useInView = (threshold = 0.1) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [inView, setInView] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
};

const MembershipPage: React.FC<MembershipPageProps> = ({ onNavigate }) => {
  const [selectedMembershipId, setSelectedMembershipId] = useState<string>('');
  const [selectedMembershipTitle, setSelectedMembershipTitle] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [isModalFadingOut, setIsModalFadingOut] = useState(false);
  const [modalData, setModalData] = useState<MembershipModalData | null>(null);
  const [modalTop, setModalTop] = useState<number | null>(null);

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', message: '', privacyAccepted: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const { ref: cardsRef, inView: cardsInView } = useInView(0.05);
  const { ref: formRef, inView: formInView } = useInView(0.05);

  const benefits = [
    {
      icon: <DollarSign className="h-5 w-5 text-[color:var(--svh-crest)]" />,
      title: 'Vergünstigte Preise',
      description: 'Kostenlose Platznutzung für aktive Mitglieder. Passive Mitglieder zahlen nur einen Bruchteil.',
    },
    {
      icon: <Calendar className="h-5 w-5 text-[color:var(--svh-crest)]" />,
      title: 'Strukturiertes Training',
      description: 'Regelmäßige Trainingsangebote und klar geplante Trainingszeiten für Mitglieder.',
    },
    {
      icon: <Trophy className="h-5 w-5 text-[color:var(--svh-crest)]" />,
      title: 'Vereinsveranstaltungen',
      description: 'Turniere, Trainingseinheiten und gesellige Vereinsveranstaltungen das ganze Jahr.',
    },
    {
      icon: <Users className="h-5 w-5 text-[color:var(--svh-crest)]" />,
      title: 'Geselliges Vereinsleben',
      description: 'Werden Sie Teil unserer Tennisgemeinschaft und finden Sie neue Spielpartner.',
    },
  ];

  const membershipCards = [
    {
      id: 'tennis',
      title: 'Tennis-Abo',
      price: 'ab 15€',
      period: 'pro Jahr',
      tag: 'Tennis',
      highlight: false,
      bullets: [
        'Kostenlose Nutzung aller Tennisplätze',
        'Vereinsturniere & Training',
        'Vereinsjugend & Ferienaktionen',
        '6 Arbeitsstunden / Saison',
      ],
      modal: {
        title: 'Tennis-Abonnement – Beiträge',
        pricing: [
          'Erwachsene: 70 €',
          'Ehepaare: 110 €',
          'Familientarif (2 Erwachsene + 1 Kind bis 18 frei): 120 €',
          'Studenten / Lehrlinge (mit Nachweis): 45 €',
          'Jugendliche bis 18: 35 €',
          'Schüler bis 14: 20 €',
          'Zweitmitgliedschaft Erwachsene: 35 €',
          'Zweitmitgliedschaft Jugendliche: 20 €',
          'Zweitmitgliedschaft Schüler: 15 €',
        ],
        notice: 'Zusätzlich ist die allgemeine SV Heinersreuth Grundmitgliedschaft erforderlich. Für das Tennis-Abonnement sind 6 Arbeitsstunden pro Saison oder eine entsprechende Ausgleichszahlung erforderlich.',
      }
    },
    {
      id: 'padel',
      title: 'Padel-Abo',
      price: 'ab 30€',
      period: 'pro Jahr',
      tag: 'Padel',
      highlight: true,
      bullets: [
        'Unbegrenzte Nutzung der Padelplätze',
        'Padel-Turniere & Events',
        'Feste Spielgruppen & Matches',
        '3 Arbeitsstunden / Saison',
      ],
      modal: {
        title: 'Padel-Abonnement – Beiträge',
        pricing: [
          'Erwachsene: 70 €',
          'Ehepaare: 110 €',
          'Familientarif (1 Kind bis 18 frei): 120 €',
          'Studenten / Lehrlinge (mit Nachweis): 50 €',
          'Jugendliche bis 18: 40 €',
          'Schüler bis 14: 30 €',
        ],
        notice: 'Zusätzlich ist die allgemeine SV Heinersreuth Grundmitgliedschaft erforderlich. 3 Arbeitsstunden pro Saison oder entsprechende Ausgleichszahlung erforderlich.',
      }
    },
    {
      id: 'kombi',
      title: 'Kombi-Abo',
      price: 'ab 40€',
      period: 'pro Jahr',
      tag: 'Tennis + Padel',
      highlight: false,
      bullets: [
        'Alle Tennis & Padel Plätze',
        'Alle Turniere & Events',
        'Ideale Wahl für Vielspieler',
        'Bestes Preis-Leistungs-Verhältnis',
      ],
      modal: {
        title: 'Kombi-Abonnement – Beiträge',
        pricing: [
          'Erwachsene: 110 €',
          'Ehepaare: 180 €',
          'Familientarif (1 Kind bis 18 frei): 210 €',
          'Studenten / Lehrlinge (mit Nachweis): 80 €',
          'Jugendliche bis 18: 60 €',
          'Schüler bis 14: 40 €',
        ],
        notice: 'Zusätzlich ist die allgemeine SV Heinersreuth Grundmitgliedschaft erforderlich.',
      }
    },
    {
      id: 'tennis-nonmember',
      title: 'Tennis Jahres-Abo',
      price: '280€',
      period: 'pro Jahr',
      tag: 'Nicht-Mitglieder',
      highlight: false,
      bullets: [
        'Keine Platzgebühren für Tennis',
        'Fixer Jahresbeitrag',
        'Keine Arbeitsstunden nötig',
      ],
      modal: {
        title: 'Tennis Jahres-Abo (Nicht-Mitglieder)',
        pricing: ['Jahresbeitrag: 280 €', 'Keine Platzgebühren', 'Keine Arbeitsstunden'],
        notice: 'Dieses Abonnement gilt für Nicht-Mitglieder und umfasst ausschließlich die Tennisplatznutzung.',
      }
    },
    {
      id: 'padel-nonmember',
      title: 'Padel Jahres-Abo',
      price: '230€',
      period: 'pro Jahr',
      tag: 'Nicht-Mitglieder',
      highlight: false,
      bullets: [
        'Keine Platzgebühren für Padel',
        'Fixer Jahresbeitrag',
        'Keine Arbeitsstunden nötig',
      ],
      modal: {
        title: 'Padel Jahres-Abo (Nicht-Mitglieder)',
        pricing: ['Jahresbeitrag: 230 €', 'Keine Platzgebühren', 'Keine Arbeitsstunden'],
        notice: 'Dieses Abonnement gilt für Nicht-Mitglieder und umfasst ausschließlich die Padelplatznutzung.',
      }
    },
  ];

  const handleSelectMembership = (cardId: string, cardTitle: string) => {
    setSelectedMembershipId(cardId);
    setSelectedMembershipTitle(cardTitle);
  };

  const handleOpenCardModal = (data: MembershipModalData, e: React.MouseEvent) => {
    e.stopPropagation();
    setModalData(data);
    setIsModalFadingOut(false);
    const viewportCenter = window.scrollY + window.innerHeight / 2;
    setModalTop(viewportCenter);
    setShowModal(true);
  };

  const closeModal = () => {
    setIsModalFadingOut(true);
    setTimeout(() => {
      setShowModal(false);
      setIsModalFadingOut(false);
      setModalData(null);
      setModalTop(null);
    }, 300);
  };

  useEffect(() => {
    return () => {};
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitStatus(null);

    if (!selectedMembershipTitle) {
      alert('Bitte wählen Sie ein Abonnement aus.');
      return;
    }
    if (!formData.privacyAccepted) {
      alert('Bitte akzeptieren Sie die Datenschutzerklärung.');
      return;
    }

    setIsSubmitting(true);

    try {
      await Promise.allSettled([
        fetch('https://n8n.cogniiq.co/webhook/membership', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone || '',
            membership_type: selectedMembershipTitle,
            message: formData.message || ''
          })
        }),
        fetch('https://hook.eu2.make.com/av9obqkrxvfw71iiuye2h9eq2clxuhy7', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone || '',
            abonnement: selectedMembershipTitle,
            message: formData.message || ''
          })
        })
      ]);
      window.location.href = '/membership/success';
    } catch {
      window.location.href = '/membership/success';
    }
  };

  const MemberCard = ({ card, index }: { card: typeof membershipCards[0]; index: number }) => {
    const isSelected = selectedMembershipId === card.id;
    const isHighlight = card.highlight;

    return (
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={cardsInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.65, delay: index * 0.09, ease: [0.22, 1, 0.36, 1] }}
        onClick={() => handleSelectMembership(card.id, card.title)}
        className={`group cursor-pointer select-none relative flex flex-col rounded-[24px] overflow-hidden transition-all duration-300 ${
          isSelected
            ? 'shadow-[0_16px_56px_rgba(220,38,38,0.18)] ring-2 ring-red-500'
            : isHighlight
              ? 'shadow-[0_8px_40px_rgba(0,0,0,0.12)] hover:shadow-[0_16px_56px_rgba(0,0,0,0.16)] ring-1 ring-gray-900/10'
              : 'shadow-[0_2px_16px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_36px_rgba(0,0,0,0.11)] ring-1 ring-gray-100'
        }`}
        style={{
          background: isHighlight
            ? 'linear-gradient(160deg, #111827 0%, #1f2937 60%, #111827 100%)'
            : '#ffffff'
        }}
      >
        {isHighlight && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-48 h-48 bg-red-600/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-600/5 rounded-full blur-2xl" />
          </div>
        )}

        <div className="relative z-10 p-7 flex flex-col h-full">
          <div className="flex items-start justify-between mb-6">
            <span className={`inline-flex items-center text-[10px] font-bold tracking-[0.16em] uppercase px-3 py-1.5 rounded-full ${
              isHighlight
                ? 'bg-white/10 text-white/70'
                : 'bg-gray-50 text-[color:var(--svh-ink-3)] border border-[color:var(--svh-border)]'
            }`}>
              {card.tag}
            </span>
            {isHighlight && !isSelected && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.12em] uppercase px-3 py-1.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/20">
                <Star className="w-2.5 h-2.5 fill-current" />
                Beliebt
              </span>
            )}
            {isSelected && (
              <div className="w-7 h-7 bg-red-500 rounded-full flex items-center justify-center shadow-lg shadow-red-500/30">
                <Check className="w-3.5 h-3.5 text-white" />
              </div>
            )}
          </div>

          <div className="mb-6">
            <div className={`text-[2.6rem] font-bold leading-none tracking-tight mb-1 ${isHighlight ? 'text-white' : 'text-gray-900'}`}>
              {card.price}
            </div>
            <div className={`text-[12px] font-medium ${isHighlight ? 'text-white/70' : 'text-[color:var(--svh-ink-3)]'}`}>
              {card.period}
            </div>
          </div>

          <div className={`text-[13px] font-bold tracking-tight mb-5 ${isHighlight ? 'text-white/90' : 'text-gray-800'}`}>
            {card.title}
          </div>

          <ul className="space-y-3 flex-1">
            {card.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  isHighlight ? 'bg-red-500/20' : 'bg-[color:var(--svh-crest-wash)]'
                }`}>
                  <Check className={`w-2.5 h-2.5 ${isHighlight ? 'text-red-400' : 'text-red-500'}`} />
                </div>
                <span className={`text-[13px] leading-relaxed ${isHighlight ? 'text-white/60' : 'text-gray-500'}`}>
                  {b}
                </span>
              </li>
            ))}
          </ul>

          <button
            onClick={(e) => handleOpenCardModal(card.modal, e)}
            className={`mt-7 w-full py-3 rounded-[16px] text-[12px] font-semibold tracking-wide transition-all duration-200 ${
              isHighlight
                ? 'bg-white/10 hover:bg-white/15 text-white/80 border border-white/10 hover:border-white/20'
                : 'bg-gray-50 hover:bg-gray-100 text-gray-500 hover:text-gray-700 border border-[color:var(--svh-border)]'
            }`}
          >
            Preisdetails anzeigen
          </button>
        </div>
      </motion.div>
    );
  };

  const NonMemberCard = ({ card, index }: { card: typeof membershipCards[0]; index: number }) => {
    const isSelected = selectedMembershipId === card.id;

    return (
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={cardsInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.65, delay: index * 0.09, ease: [0.22, 1, 0.36, 1] }}
        onClick={() => handleSelectMembership(card.id, card.title)}
        className={`group cursor-pointer select-none relative flex flex-col rounded-[16px] bg-white transition-all duration-300 ${
          isSelected
            ? 'shadow-[0_8px_40px_rgba(220,38,38,0.14)] ring-2 ring-red-500'
            : 'shadow-[0_2px_12px_rgba(0,0,0,0.05)] hover:shadow-[0_6px_28px_rgba(0,0,0,0.1)] ring-1 ring-gray-100'
        }`}
      >
        <div className="p-6 flex flex-col h-full">
          <div className="flex items-center justify-between mb-5">
            <span className="inline-flex items-center text-[12px] font-bold tracking-[0.1em] uppercase px-3 py-1.5 rounded-full bg-gray-50 text-[color:var(--svh-ink-3)] border border-[color:var(--svh-border)]">
              {card.tag}
            </span>
            {isSelected && (
              <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center shadow-md shadow-red-500/25">
                <Check className="w-3 h-3 text-white" />
              </div>
            )}
          </div>

          <div className="mb-5">
            <div className="text-[2.2rem] font-bold leading-none tracking-tight text-gray-900 mb-1">
              {card.price}
            </div>
            <div className="text-[13px] font-medium text-[color:var(--svh-ink-3)]">{card.period}</div>
          </div>

          <div className="text-[13px] font-bold tracking-tight text-gray-800 mb-4">{card.title}</div>

          <ul className="space-y-2.5 flex-1">
            {card.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <div className="w-4 h-4 rounded-full bg-[color:var(--svh-crest-wash)] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-2.5 h-2.5 text-red-500" />
                </div>
                <span className="text-[13px] leading-relaxed text-gray-500">{b}</span>
              </li>
            ))}
          </ul>

          <button
            onClick={(e) => handleOpenCardModal(card.modal, e)}
            className="mt-6 w-full py-2.5 rounded-[10px] text-[12px] font-semibold tracking-wide bg-gray-50 hover:bg-gray-100 text-gray-500 hover:text-gray-700 border border-[color:var(--svh-border)] transition-all duration-200"
          >
            Preisdetails anzeigen
          </button>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-white relative">
      <section className="relative h-[55vh] min-h-[420px] overflow-hidden bg-black">
        <img src="/photos/tennis-ball.jpg" alt="Mitgliedschaft" className="absolute inset-0 w-full h-full object-cover scale-[1.06] opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/90" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />

        <div className="relative z-10 h-full flex flex-col justify-end pb-16 sm:pb-20 px-6 sm:px-10 max-w-7xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-7 h-[1.5px] bg-[color:var(--svh-crest)]" />
              <span className="svh-eyebrow">SV Heinersreuth</span>
            </div>
            <h1 className="text-[clamp(2.8rem,6vw,5rem)] font-bold text-white leading-[0.95] tracking-tight mb-4">
              Mitgliedschaft
            </h1>
            <p className="text-white/45 text-[16px] max-w-md font-light leading-relaxed">
              Sichern Sie sich exklusive Vorteile und bevorzugte Konditionen.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 sm:py-24 bg-white border-y border-[color:var(--svh-border)]">
        <div className="max-w-7xl mx-auto px-5 sm:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.55, delay: index * 0.08 }}
                className="bg-white rounded-[16px] p-6 border border-[color:var(--svh-border)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.07)] transition-all duration-300"
              >
                <div className="w-10 h-10 bg-[color:var(--svh-crest-wash)] rounded-[10px] flex items-center justify-center mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-[14px] font-bold text-gray-900 mb-2 tracking-tight">{benefit.title}</h3>
                <p className="text-[13px] text-[color:var(--svh-ink-3)] leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section ref={cardsRef} className="svh-band svh-band--canvas svh-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-10">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={cardsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="svh-split lg:items-end mb-14 sm:mb-20"
          >
            <div className="svh-opener">
              <div className="svh-opener__tag">
                <span className="svh-label">Abonnements</span>
              </div>
              <h2 className="svh-display-lg">
                Wählen Sie<br />Ihr Paket
              </h2>
            </div>
            <p className="svh-lead max-w-sm lg:text-right lg:justify-self-end">
              Karte antippen zum Auswählen.
            </p>
          </motion.div>

          <div className="mb-5">
            <motion.p
              initial={{ opacity: 0 }}
              animate={cardsInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="svh-label mb-5 pb-3 border-b border-[color:var(--svh-border)]"
            >
              Vereinsmitglieder
            </motion.p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {membershipCards.slice(0, 3).map((card, i) => (
                <MemberCard key={card.id} card={card} index={i} />
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={cardsInView ? { opacity: 1, scaleX: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="origin-left h-px bg-[color:var(--svh-border)] my-14 sm:my-16"
          />

          <div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={cardsInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="svh-label mb-5 pb-3 border-b border-[color:var(--svh-border)]"
            >
              Nicht-Mitglieder
            </motion.p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl">
              {membershipCards.slice(3).map((card, i) => (
                <NonMemberCard key={card.id} card={card} index={i + 3} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <AnimatePresence>
        {showModal && modalData && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: isModalFadingOut ? 0 : 1 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
              onClick={closeModal}
            />
            {modalTop !== null && modalData && (
              <div
                className="absolute inset-x-0 z-50 flex justify-center p-4 sm:p-6"
                style={{ top: modalTop - window.innerHeight / 2 }}
                onClick={closeModal}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.94, y: 16 }}
                  animate={{ opacity: isModalFadingOut ? 0 : 1, scale: isModalFadingOut ? 0.94 : 1, y: isModalFadingOut ? 16 : 0 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="w-full max-w-[95vw] sm:max-w-lg mx-auto bg-white rounded-[24px] shadow-[0_32px_80px_rgba(0,0,0,0.25)] max-h-[90vh] sm:max-h-[85vh] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="relative p-6 sm:p-8">
                    <button
                      onClick={closeModal}
                      className="absolute top-5 right-5 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                    >
                      <X className="w-4 h-4 text-gray-600" />
                    </button>
                    <div className="pr-10">
                      <h3 className="text-[1.2rem] font-bold text-gray-900 mb-5 tracking-tight">{modalData.title}</h3>
                      <ul className="space-y-2.5 mb-6">
                        {modalData.pricing.map((p: string, i: number) => (
                          <li key={i} className="text-gray-700 flex items-start text-[13px] leading-relaxed gap-2.5">
                            <Check className="h-3.5 w-3.5 text-[color:var(--svh-crest)] mt-0.5 flex-shrink-0" />
                            <span>{p}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="bg-gray-50 border border-[color:var(--svh-border)] rounded-[16px] p-4 mb-6">
                        <p className="text-[13px] text-[color:var(--svh-ink-3)] leading-relaxed">
                          <span className="font-semibold text-gray-700">Hinweis:</span> {modalData.notice}
                        </p>
                      </div>
                      <button
                        onClick={closeModal}
                        className="w-full py-3 bg-gray-900 hover:bg-gray-800 text-white rounded-[16px] font-semibold text-[13px] transition-colors"
                      >
                        Schließen
                      </button>
                    </div>
                  </div>
                </motion.div>
              </div>
            )}
          </>
        )}
      </AnimatePresence>

      <section ref={formRef} className="py-20 sm:py-24 bg-white border-t border-[color:var(--svh-border)]">
        <div className="max-w-2xl mx-auto px-5 sm:px-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={formInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mb-10"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-7 h-[1.5px] bg-[color:var(--svh-crest)]" />
              <span className="svh-eyebrow">Interesse?</span>
            </div>
            <h2 className="text-[clamp(1.8rem,3.5vw,2.8rem)] font-bold text-gray-900 tracking-tight mb-2">
              Jetzt anfragen
            </h2>
            <p className="text-[color:var(--svh-ink-2)] text-[15px]">Persönliche Beratung oder direkte Mitgliedsanfrage.</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={formInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="bg-white rounded-[24px] shadow-[0_2px_24px_rgba(0,0,0,0.06)] border border-[color:var(--svh-border)] p-7 sm:p-9"
          >
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className={`relative p-4 rounded-[16px] border-2 transition-all duration-300 ${
                selectedMembershipTitle
                  ? 'bg-[color:var(--svh-crest-wash)] border-red-200'
                  : 'bg-gray-50 border-gray-200 border-dashed'
              }`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                      selectedMembershipTitle ? 'bg-red-600' : 'bg-gray-300'
                    }`}>
                      {selectedMembershipTitle ? (
                        <Check className="w-3.5 h-3.5 text-white" />
                      ) : (
                        <Users className="w-3.5 h-3.5 text-white" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="svh-label">
                        Gewähltes Abonnement {!selectedMembershipTitle && <span className="text-red-500">*</span>}
                      </p>
                      <p className={`text-[13px] font-semibold truncate ${selectedMembershipTitle ? 'text-[color:var(--svh-ink)]' : 'text-[color:var(--svh-ink-3)]'}`}>
                        {selectedMembershipTitle || 'Bitte oben eine Karte auswählen'}
                      </p>
                    </div>
                  </div>
                  {selectedMembershipTitle && (
                    <button
                      type="button"
                      onClick={() => { setSelectedMembershipId(''); setSelectedMembershipTitle(''); }}
                      className="text-[color:var(--svh-ink-3)] hover:text-[color:var(--svh-crest)] transition-colors p-1 rounded-full hover:bg-white flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[12px] font-semibold text-gray-600 mb-1.5">
                    <User className="inline w-3 h-3 mr-1" />Name *
                  </label>
                  <input
                    type="text" name="name" value={formData.name} onChange={handleInputChange} required
                    className="w-full px-4 py-3 border border-gray-200 rounded-[10px] focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-[13px] transition-all bg-gray-50/50 focus:bg-white"
                    placeholder="Ihr vollständiger Name"
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-gray-600 mb-1.5">
                    <Mail className="inline w-3 h-3 mr-1" />E-Mail *
                  </label>
                  <input
                    type="email" name="email" value={formData.email} onChange={handleInputChange} required
                    className="w-full px-4 py-3 border border-gray-200 rounded-[10px] focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-[13px] transition-all bg-gray-50/50 focus:bg-white"
                    placeholder="ihre.email@beispiel.de"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[12px] font-semibold text-gray-600 mb-1.5">
                  <Phone className="inline w-3 h-3 mr-1" />Telefonnummer
                </label>
                <input
                  type="tel" name="phone" value={formData.phone} onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-[10px] focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-[13px] transition-all bg-gray-50/50 focus:bg-white"
                  placeholder="+49 123 456789"
                />
              </div>

              <div>
                <label className="block text-[12px] font-semibold text-gray-600 mb-1.5">Nachricht</label>
                <textarea
                  name="message" rows={4} value={formData.message} onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-[10px] focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-[13px] transition-all resize-none bg-gray-50/50 focus:bg-white"
                  placeholder="Für welches Abonnement interessieren Sie sich?"
                />
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox" id="privacy" name="privacyAccepted"
                  checked={formData.privacyAccepted} onChange={handleInputChange}
                  required disabled={isSubmitting}
                  className="mt-1 w-4 h-4 rounded border-gray-300 text-[color:var(--svh-crest)] focus:ring-red-500"
                />
                <label htmlFor="privacy" className="text-[13px] text-[color:var(--svh-ink-3)] leading-relaxed">
                  Ich habe die{' '}
                  <button
                    type="button" className="text-[color:var(--svh-crest)] underline underline-offset-2"
                    onClick={() => {
                      if (typeof onNavigate === 'function') {
                        onNavigate('datenschutz');
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                  >
                    Datenschutzerklärung
                  </button>{' '}
                  gelesen und akzeptiere diese.
                </label>
              </div>

              <button
                type="submit" disabled={isSubmitting}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white py-3.5 text-[13px] font-semibold rounded-[16px] transition-all duration-200 inline-flex items-center justify-center gap-2 disabled:opacity-60 tracking-wide"
              >
                {isSubmitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /><span>Wird gesendet...</span></>
                ) : (
                  <><span>Jetzt anfragen</span><ArrowRight size={14} /></>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MembershipPage;
